import { Box, Typography, Stack, Button, IconButton, Divider } from '@mui/material';
import { useHistory } from 'react-router-dom';

import AccountingOnboardingCompletionSVG from 'assets/img/icons/CheckLarge.svg';
import Tag from 'modules/common/components/MUI/Tag';
import PendingClockIcon from 'modules/common/components/Icons/PendingClockIcon';
import ViewListIcon from 'modules/common/components/Icons/ViewListIcon';
import MailIcon from 'modules/common/components/Icons/MailIcon';
import CloseXIcon from 'modules/common/components/Icons/CloseXIcon';

interface CompletionInfoRowProps {
    readonly icon: React.ReactNode;
    readonly iconBackground: string;
    readonly title: string;
    readonly description: string;
    readonly tag?: React.ReactNode;
}

function CompletionInfoRow({ icon, iconBackground, title, description, tag = null }: CompletionInfoRowProps) {
    return (
        <Stack direction="row" spacing={2} sx={{ py: 2.5 }}>
            <Box
                sx={{
                    width: 32,
                    height: 32,
                    flexShrink: 0,
                    borderRadius: '50%',
                    backgroundColor: iconBackground,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                {icon}
            </Box>
            <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="bodySemiBold" color="text.primary">
                        {title}
                    </Typography>
                    {tag}
                </Stack>
                <Typography variant="body1" color="text.secondary">
                    {description}
                </Typography>
            </Stack>
        </Stack>
    );
}

interface AccountingSetupCompletionProps {
    onComplete: () => void;
}

function AccountingSetupCompletion({ onComplete }: AccountingSetupCompletionProps) {
    const history = useHistory();

    const handleFinish = () => {
        // Mark onboarding as completed in backend
        onComplete();

        // Redirect to bookkeeping dashboard
        history.push('/bookkeeping/dashboard');
    };

    return (
        <Box
            sx={{
                width: '100%',
                height: '100vh',
                backgroundColor: '#F6F3EB', // Exact Figma color
                position: 'relative',
                overflow: 'hidden',
                borderRadius: (theme) => theme.spacing(0.5),
            }}>
            {/* Close button - positioned in top-right corner like Figma */}
            <IconButton
                aria-label="Close"
                onClick={() => history.push('/bookkeeping/dashboard')}
                sx={{
                    position: 'absolute',
                    right: (theme) => theme.spacing(2.5),
                    top: (theme) => theme.spacing(2.5),
                    width: (theme) => theme.spacing(4),
                    height: (theme) => theme.spacing(4),
                    backgroundColor: 'action.hover',
                    borderRadius: '50%',
                    '&:hover': {
                        backgroundColor: 'action.selected',
                    },
                }}>
                <CloseXIcon
                    sx={{
                        width: (theme) => theme.spacing(2),
                        height: (theme) => theme.spacing(2),
                        color: 'text.primary',
                    }}
                />
            </IconButton>

            {/* Main content container - perfectly centered like Figma */}
            <Stack
                direction="column"
                alignItems="center"
                spacing={4}
                sx={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '560px',
                    maxWidth: '90vw', // Responsive width
                    backgroundColor: 'background.paper',
                    borderRadius: (theme) => theme.spacing(2),
                    padding: (theme) => theme.spacing(4),
                }}>
                <Typography variant="h1" align="center" color="text.primary" sx={{ alignSelf: 'stretch' }}>
                    Your accounting setup is complete
                </Typography>

                <Box
                    sx={{
                        width: (theme) => theme.spacing(12.5),
                        height: (theme) => theme.spacing(12.5),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                    <img
                        src={AccountingOnboardingCompletionSVG}
                        alt="Completion checkmark"
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                        }}
                    />
                </Box>

                {/* Status/next-steps info rows - matches Figma */}
                <Box sx={{ alignSelf: 'stretch' }}>
                    <CompletionInfoRow
                        icon={<PendingClockIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
                        iconBackground="purple.100"
                        title="Collective review"
                        tag={<Tag color="primary" variant="filled" label="In progress" />}
                        description="Our team is checking everything for accuracy. We’ll email you if we need any follow-up information."
                    />
                    <Divider />
                    <CompletionInfoRow
                        icon={<ViewListIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
                        iconBackground="cement.200"
                        title="Action items"
                        description="Anything outstanding will appear directly in your tasks."
                    />
                    <Divider />
                    <CompletionInfoRow
                        icon={<MailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
                        iconBackground="cement.200"
                        title="Confirmation email"
                        description="You'll hear from us once your books are in order."
                    />
                </Box>

                {/* Finish button - exact Figma structure */}
                <Button
                    onClick={handleFinish}
                    variant="contained"
                    size="large"
                    sx={{
                        alignSelf: 'stretch',
                        height: (theme) => theme.spacing(6),
                        padding: (theme) => theme.spacing(1.5, 3.5),
                        borderRadius: (theme) => theme.spacing(1.5),
                    }}>
                    Sounds good
                </Button>
            </Stack>
        </Box>
    );
}

export default AccountingSetupCompletion;
